import { useNavigate } from "#src/Routers/hooks";
import { FormCategoriesTableFilterArea } from "#src/batteries-included-components/FilterAreas/FormsFilterAreas";
import { useTableSortingAndPagination } from "#src/components/Redux/reducers/tableStateReducer";
import { useStickyState } from "#src/hooks/useStickyState";
import { linkToFormCategoryDetails } from "#src/routes/forms/categories/[categoryId]";
import { useQuery } from "@tanstack/react-query";
import {
  DateDataDisplay,
  EmptyState,
  Flex,
  GridCard,
  Pagination,
  Panel,
  Skeleton,
  type StorageKeys,
} from "@validereinc/common-components";
import { FormCategoryAdapter, Resources } from "@validereinc/domain";
import { DateFormats } from "@validereinc/utilities";
import React from "react";

const { Container, Item } = Flex;

export const FormCategoriesPanel = ({
  filterConfigStorageKey,
  tableStateStorageKey,
}: Pick<StorageKeys, "filterConfigStorageKey" | "tableStateStorageKey">) => {
  const navigate = useNavigate();
  const [filters] = useStickyState<{ name?: string }>(
    {},
    filterConfigStorageKey
  );
  const [{ ...pagination }, setPagination] = useTableSortingAndPagination(
    {
      pageSize: 30,
      pageSizeText: "cards per page",
    },
    filters,
    tableStateStorageKey
  );

  const queryFn = async ({ queryKey }: any) => {
    const [_, { dataKey: _dataKey, ...restFilters }, { page, pageSize }] =
      queryKey;

    const { data: newFormCategories, total_entries } =
      await FormCategoryAdapter.getList({
        filters: {
          status: "active",
          ...restFilters,
        },
        page,
        pageSize: pageSize,
      });

    setPagination({ total: total_entries });

    return newFormCategories;
  };

  const query = useQuery({
    queryKey: [Resources.FORM_CATEGORY, filters, pagination],
    queryFn,
  });

  const onCardClick = (formCategoryId: string) => {
    navigate({
      pathname: linkToFormCategoryDetails(formCategoryId),
    });
  };

  return (
    <Panel
      title="Categories"
      loaded={!query?.isLoading}
      actionRow={[
        <FormCategoriesTableFilterArea
          key="filters"
          filterConfigStorageKey={filterConfigStorageKey}
          filterDrawerContentSlot={null}
        />,
      ]}
    >
      {query?.data?.length === 0 ? (
        <EmptyState
          title="There are no categories to display"
          suggestion="Please try adjusting your filters"
        />
      ) : (
        <>
          <Container variant="grid">
            {query.data?.map(({ id, name, updated_at }) => (
              <Item key={`form-category-${id}`}>
                <GridCard.Container
                  title={name}
                  onClick={() => onCardClick(id)}
                  isLoading={query?.isLoading}
                  slotFooter={(props) => (
                    <GridCard.Footer
                      {...props}
                      slotLeft={(props) => (
                        <Skeleton
                          isLoading={props.isLoading}
                          isBusy={props.isBusy}
                        >
                          <p {...props}>
                            Last updated on&nbsp;
                            <DateDataDisplay
                              as="span"
                              isInline
                              value={updated_at}
                              displayFormat={DateFormats.DATE}
                            />
                          </p>
                        </Skeleton>
                      )}
                    />
                  )}
                />
              </Item>
            ))}
          </Container>

          <Pagination
            {...pagination}
            onChange={setPagination}
            style={{ marginTop: 8 }}
          />
        </>
      )}
    </Panel>
  );
};
